// SCR-STAFF-934 — وضعیت لحظه‌ای منابع (US-ORG-022، AC-ORG-022.1): کارت هر اتاق، واحد و دستگاه شعبه با وضعیت Free، Occupied، Blocked،
// UnderMaintenance یا Inactive و زمان آزاد شدن بعدی؛ بازخوانی هر ۳۰ ثانیه و پس از هر تغییر نوبت (اعلان زنده SCH) — C-AGENT-742.
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { unwrap, type ResourceStatusView } from '@phoenix/api-client';
import { useTenantSession } from '@phoenix/auth';
import { useTranslation } from '@phoenix/i18n';
import { EmptyState, ErrorState, JalaliDate, ListPage, LoadingState, StatusChip } from '@phoenix/ui';
import { useQuery } from '@tanstack/react-query';
import { useAppointmentsChanged } from '../scheduling/realtime';
import { BranchFilter, useBranchSelection } from './common';
import { deviceKeys, liveTone, ReportIssueButton } from './deviceCommon';

export const SCR_STAFF_934 = 'SCR-STAFF-934';

export function ResourceStatusPage() {
  const { t } = useTranslation('org');
  const { tenantApi, session } = useTenantSession();
  const branches = useBranchSelection();
  const status = useQuery({
    queryKey: deviceKeys.status(session?.tenantId, branches.branchId),
    queryFn: () =>
      unwrap(tenantApi.GET('/api/v1/organization/resource-status', { params: { query: { branchId: branches.branchId! } } })),
    enabled: Boolean(branches.branchId),
    refetchInterval: 30_000,
  });
  useAppointmentsChanged((message) => {
    if (message.branchId === branches.branchId) {
      void status.refetch();
    }
  });

  const groups: { key: string; rows: ResourceStatusView[] }[] = [
    { key: 'rooms', rows: (status.data?.items ?? []).filter((row) => row.resourceKind !== 'Device') },
    { key: 'devices', rows: (status.data?.items ?? []).filter((row) => row.resourceKind === 'Device') },
  ];

  return (
    <ListPage
      screenId={SCR_STAFF_934}
      titleKey="org:live.title"
      subtitleKey="org:live.subtitle"
      filters={<BranchFilter options={branches.options} value={branches.branchId} onChange={branches.setBranchId} />}
    >
      {branches.options.length === 0 && <EmptyState titleKey="org:no_branch" />}
      {status.isPending && branches.branchId && <LoadingState rows={3} />}
      {status.error ? <ErrorState error={status.error} onRetry={() => void status.refetch()} /> : null}
      {status.data && status.data.items.length === 0 && <EmptyState titleKey="org:live.empty" />}
      {groups
        .filter((group) => group.rows.length > 0)
        .map((group) => (
          <Stack key={group.key} spacing={1} sx={{ mb: 3 }}>
            <Typography variant="h6" component="h2">
              {t(`org:live.groups.${group.key}`)}
            </Typography>
            <Grid container spacing={2}>
              {group.rows.map((row) => (
                <Grid key={row.resourceId} size={{ xs: 12, sm: 6, md: 4, lg: 3 }}>
                  <Card variant="outlined" data-testid={`live-${row.resourceId}`} data-status={row.status}>
                    <CardContent>
                      <Stack spacing={1}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
                          <Typography variant="subtitle1" component="h3">
                            {row.name}
                          </Typography>
                          <Chip size="small" variant="outlined" label={t(`org:resource_kind.${row.resourceKind}`)} />
                        </Stack>
                        <StatusChip tone={liveTone(row.status)} labelKey={`org:live_status.${row.status}`} />
                        {row.nextFreeAt && (
                          <Typography variant="caption" color="text.secondary">
                            {t('org:live.next_free')} <JalaliDate value={row.nextFreeAt} withTime />
                          </Typography>
                        )}
                        {row.status !== 'Inactive' && (
                          <ReportIssueButton resourceKind={row.resourceKind} resourceId={row.resourceId} resourceName={row.name} variant="text" onReported={() => void status.refetch()} />
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Stack>
        ))}
    </ListPage>
  );
}
